import { z } from 'zod';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { postingStatusSchema } from './inventory.contract.js';
import { needsReviewQuerySchema, pdaOfflineInfoSchema, pdaPurchaseInfoSchema, priceStatusSchema, purchaseSourceSchema, } from './pda.contract.js';
import { actorSchema, branchRefSchema, businessDateSchema, containerTypeSchema, customerLinkErrorSchema, customerLinkStatusSchema, debtKindSchema, entryModeSchema, fractionSchema, fuelMoneySchema, lineSchema, nonNegativeMoneySchema, notesSchema, positiveMoneySchema, phoneVerificationIdSchema, productSchema, quantityLinesSchema, transactionListQuerySchema, } from './primitives.js';
// --- transaction rows (fuel.md section 5.2) ---------------------------------
const rowBase = z.object({
    id: z.string(),
    number: z.number().int(),
    reference: z.string(),
    product: productSchema,
    businessDate: businessDateSchema,
    branch: branchRefSchema,
    createdBy: actorSchema,
    createdAt: z.iso.datetime({ offset: true }),
    deletedAt: z.iso.datetime({ offset: true }).nullable(),
    deletedBy: actorSchema.nullable(),
});
/**
 * The posting columns of a purchase, sale, debt movement, payment or receipt row (owner tickets
 * KAN-39 and KAN-40). `entryBusinessDate` is the business date at entry, which
 * التاريخ shows; `businessDate` is the shift the row is posted to. `postedAt` is
 * تاريخ الترحيل: the posting time, or the entry time of a row posted at entry;
 * null while the row is pending («غير مرحلة»).
 */
const postingColumnsSchema = z.object({
    postingStatus: postingStatusSchema,
    entryBusinessDate: businessDateSchema,
    postedAt: z.iso.datetime({ offset: true }).nullable(),
});
/**
 * The shift a carried balance opened (KAN-59): ACTIVE while its business date is
 * still the branch's open day and not closed; CLOSED once that day was closed,
 * with the close time (null for a past day that has no close record).
 */
export const revolvingBalanceShiftSchema = z.object({
    state: z.enum(['ACTIVE', 'CLOSED']),
    closedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const revolvingBalanceRowSchema = rowBase.extend({
    cashBalance: fuelMoneySchema,
    lessJerrycans: nonNegativeMoneySchema,
    notes: z.string().nullable(),
    lines: z.array(lineSchema),
    shift: revolvingBalanceShiftSchema,
});
export const quantityTransactionRowSchema = rowBase.extend({
    barrelPrice: fuelMoneySchema,
    jerrycanPrice: fuelMoneySchema,
    total: fuelMoneySchema,
    lessJerrycans: nonNegativeMoneySchema,
    finalTotal: fuelMoneySchema,
    notes: z.string().nullable(),
    lines: z.array(lineSchema),
});
/**
 * A purchase row adds the PDA fields (fuel-pda.md section 6.1): where it was
 * entered, whether its prices are complete (a PENDING purchase has amounts of
 * 0 that the client shows as «بانتظار التسعير») and the PDA details.
 */
export const purchaseRowSchema = quantityTransactionRowSchema
    .extend(postingColumnsSchema.shape)
    .extend({
    source: purchaseSourceSchema,
    priceStatus: priceStatusSchema,
    pda: pdaPurchaseInfoSchema.nullable(),
    /**
     * «موظف التسعير»: the office employee who completed the prices of a PDA
     * purchase (the latest PRICED_BY participant) and when; null until priced
     * and for operator purchases (T2).
     */
    pricedBy: actorSchema.nullable(),
    pricedAt: z.iso.datetime({ offset: true }).nullable(),
    /** Synced from a PDA in offline mode (fuel-pda.md section 13); null otherwise. */
    offline: pdaOfflineInfoSchema.nullable().default(null),
});
export const saleRowSchema = quantityTransactionRowSchema
    .extend(postingColumnsSchema.shape)
    .extend({
    /** PDA for a sale started on the PDA (fuel-pda.md section 6.1a, KAN-37). */
    source: purchaseSourceSchema,
    /** PENDING until the operator lookup completes the prices; amounts are 0 meanwhile. */
    priceStatus: priceStatusSchema,
    /** The PDA customer and quantities of a PDA sale; null for an office sale. */
    pda: pdaPurchaseInfoSchema.nullable(),
    deliveryStatus: z.enum(['PENDING', 'DELIVERED']),
    deliveredAt: z.iso.datetime({ offset: true }).nullable(),
    /** «موظف التسليم»: the latest DELIVERED_BY participant (PDA or dashboard); null while pending (T2). */
    deliveredBy: actorSchema.nullable(),
    /** Delivered from a PDA in offline mode (fuel-pda.md section 13); null otherwise. */
    offline: pdaOfflineInfoSchema.nullable().default(null),
});
export const debtMovementRowSchema = rowBase.extend(postingColumnsSchema.shape).extend({
    kind: debtKindSchema,
    entryMode: entryModeSchema,
    debtor: z.object({ id: z.string(), name: z.string() }),
    vehicle: z.object({ id: z.string(), plate: z.string() }).nullable(),
    barrelPrice: fuelMoneySchema.nullable(),
    jerrycanPrice: fuelMoneySchema.nullable(),
    total: fuelMoneySchema,
    lessJerrycans: nonNegativeMoneySchema,
    finalTotal: fuelMoneySchema,
    isPriorBalance: z.boolean(),
    /** Charges only; null on repayments. */
    deliveryStatus: z.enum(['PENDING', 'DELIVERED']).nullable(),
    deliveredAt: z.iso.datetime({ offset: true }).nullable(),
    /** «موظف التسليم»: the latest DELIVERED_BY participant; null while pending and on repayments (T2). */
    deliveredBy: actorSchema.nullable(),
    /** Status of the latest message for this movement, if any. */
    messageStatus: z
        .enum(['queued', 'sent', 'delivered', 'read', 'failed', 'invalid_phone'])
        .nullable(),
    revision: z.number().int(),
    notes: z.string().nullable(),
    lines: z.array(lineSchema),
});
export const cashVoucherRowSchema = rowBase.extend(postingColumnsSchema.shape).extend({
    amount: fuelMoneySchema,
    personName: z.string(),
    statement: z.string(),
});
export const paymentRowSchema = cashVoucherRowSchema;
export const receiptRowSchema = cashVoucherRowSchema;
// --- totals (present only with MOVEMENT_TOTALS_VIEW) --------------------------
export const moneyTotalsSchema = z.object({ sum: fuelMoneySchema });
export const quantityTotalsSchema = z.object({
    sum: fuelMoneySchema,
    barrels: z.string(),
    jerrycans: z.string(),
});
export const debtTotalsSchema = z.object({
    charges: fuelMoneySchema,
    repayments: fuelMoneySchema,
    balance: fuelMoneySchema,
    barrels: z.string(),
    jerrycans: z.string(),
});
/**
 * A transaction list page (fuel.md section 5.2). It is not the generic
 * `{ data, meta }` page because it also carries the totals of the whole
 * filtered set and, without MOVEMENT_HISTORY_VIEW, the fixed day. It travels
 * as the envelope's `data`.
 */
export const listResponse = (row, totals) => z.object({
    rows: z.array(row),
    pagination: z.object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
        totalPages: z.number().int().nonnegative(),
    }),
    /** Set when the list is fixed to the session branch and its business date. */
    fixedDay: z.object({ branch: branchRefSchema, businessDate: businessDateSchema }).nullable(),
    /** Present only with MOVEMENT_TOTALS_VIEW. */
    totals: totals.nullable(),
});
/** Sales and debt movements also filter by delivery status and participant. */
export const saleListQuerySchema = transactionListQuerySchema.extend({
    deliveryStatus: z.enum(['PENDING', 'DELIVERED']).optional(),
    participantUserId: z.string().optional(),
    /** «تحتاج مراجعة» (fuel-pda.md section 13.8). */
    needsReview: needsReviewQuerySchema,
});
export const debtMovementListQuerySchema = transactionListQuerySchema.extend({
    deliveryStatus: z.enum(['PENDING', 'DELIVERED']).optional(),
    participantUserId: z.string().optional(),
    debtorId: z.string().optional(),
    vehicleId: z.string().optional(),
    kind: debtKindSchema.optional(),
    /** حالة الترحيل (KAN-28): pending debt sales are listed with their status. */
    postingStatus: postingStatusSchema.optional(),
});
// --- debt movements (fuel.md section 5.3) --------------------------------------
export const createManualDebtMovementSchema = z.object({
    branchId: z.string().min(1),
    product: productSchema,
    kind: debtKindSchema,
    debtorId: z.string().min(1),
    vehicleId: z.string().min(1).optional(),
    amount: positiveMoneySchema,
    barrelPrice: positiveMoneySchema.optional(),
    jerrycanPrice: positiveMoneySchema.optional(),
    lines: quantityLinesSchema.optional(),
    /** «إلا جركل»: deducted jerrycans, typed without a sign. */
    lessJerrycans: nonNegativeMoneySchema.optional(),
    notes: notesSchema,
    isPriorBalance: z.boolean().default(false),
});
/** Edit keeps branch and product; the form follows the movement's entry mode. */
export const updateDebtMovementSchema = z.discriminatedUnion('entryMode', [
    z.object({
        entryMode: z.literal('QUANTITIES'),
        kind: z.literal('CHARGE'),
        debtorId: z.string().min(1),
        vehicleId: z.string().min(1).nullable(),
        barrelPrice: positiveMoneySchema,
        jerrycanPrice: positiveMoneySchema,
        lines: quantityLinesSchema,
        lessJerrycans: nonNegativeMoneySchema,
        notes: notesSchema,
    }),
    z.object({
        entryMode: z.literal('AMOUNT'),
        kind: debtKindSchema,
        debtorId: z.string().min(1),
        vehicleId: z.string().min(1).nullable(),
        amount: positiveMoneySchema,
        barrelPrice: positiveMoneySchema.nullable(),
        jerrycanPrice: positiveMoneySchema.nullable(),
        lines: quantityLinesSchema,
        lessJerrycans: nonNegativeMoneySchema,
        notes: notesSchema,
        isPriorBalance: z.boolean(),
    }),
]);
// --- debtors and vehicles (fuel.md section 5.4) ---------------------------------
export const debtorListQuerySchema = paginationQuerySchema.extend({
    /** A branch id, or `SHARED` for debtors of every branch. */
    branchId: z.string().optional(),
    linkStatus: customerLinkStatusSchema.optional(),
    /** Creation date range in Asia/Amman, both ends inclusive (legacy filter-debtors). */
    from: businessDateSchema.optional(),
    to: businessDateSchema.optional(),
});
export const debtorRowSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    name: z.string(),
    phone: z.string().nullable(),
    legacyPhone: z.string().nullable(),
    branch: branchRefSchema.nullable(),
    balance: fuelMoneySchema,
    debtLimit: fuelMoneySchema,
    linkStatus: customerLinkStatusSchema,
    /** The final Customers refusal of a «بانتظار الربط» debtor, shown with the row (fuel.md section 8.3). */
    linkError: customerLinkErrorSchema.nullable(),
    /** «رقم مؤكد»: the phone was verified by a WhatsApp code and Customers applied the receipt. */
    phoneVerified: z.boolean(),
    /**
     * «إعادة التحقق» is offered: the last registration or phone change failed
     * because its verification receipt expired or was refused.
     */
    reverifyAvailable: z.boolean(),
    notes: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    /** «تم الإنشاء بواسطة»: who created the account; null when unknown (migrated debtors). */
    createdBy: actorSchema.nullable(),
});
export const saveDebtorSchema = z.object({
    name: z.string().trim().min(1).max(200),
    /**
     * A mobile only, normalised to E.164 (customers.md section 9): any accepted
     * form (`0791234567`, `00962…`, Arabic-Indic digits) is stored as
     * `+962791234567`; a landline is refused with `PHONE_NOT_MOBILE`.
     */
    phone: phoneSchema,
    debtLimit: nonNegativeMoneySchema,
    branchId: z.string().min(1).nullable(),
    notes: notesSchema,
    /** The verified WhatsApp code of this save, when the switch asks for one (fuel.md section 8.3). */
    phoneVerificationId: phoneVerificationIdSchema.optional(),
});
/**
 * PATCH /debtors/:id: the same fields, but a stored phone of an excluded
 * country is read too, so the service saves it unchanged and refuses it with
 * PHONE_COUNTRY_NOT_ALLOWED only when it changed (customers.md section 9.7).
 */
export const updateDebtorSchema = saveDebtorSchema.extend({ phone: storedPhoneSchema });
export const adjustDebtorBalanceSchema = z.object({
    /** Signed: positive increases the debt. */
    amount: fuelMoneySchema.refine((v) => /[1-9]/.test(v), 'must not be zero'),
    notes: notesSchema,
});
export const vehicleRowSchema = z.object({
    id: z.string(),
    plate: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
});
export const createVehicleSchema = z.object({ plate: z.string().trim().min(1).max(100) });
// --- closes, trash, SMS, overview -------------------------------------------------
export const closeRowSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    reference: z.string(),
    businessDate: businessDateSchema,
    branch: branchRefSchema,
    createdBy: actorSchema,
    createdAt: z.iso.datetime({ offset: true }),
    notes: z.string().nullable(),
});
export const closeListQuerySchema = paginationQuerySchema.extend({
    branchId: z.string().optional(),
    from: businessDateSchema.optional(),
    to: businessDateSchema.optional(),
});
export const trashTypeSchema = z.enum([
    'REVOLVING_BALANCE',
    'SALE',
    'PURCHASE',
    'DEBT_MOVEMENT',
    'PAYMENT',
    'RECEIPT',
    'DEBTOR',
]);
export const trashListQuerySchema = paginationQuerySchema.extend({ type: trashTypeSchema });
export const trashRowSchema = z.object({
    id: z.string(),
    type: trashTypeSchema,
    number: z.number().int(),
    reference: z.string().nullable(),
    businessDate: businessDateSchema.nullable(),
    branch: branchRefSchema.nullable(),
    createdBy: actorSchema.nullable(),
    /**
     * «العميل»: the debtor of a debt movement, the purchase customer of a PDA
     * purchase, or the debtor itself; null where the family has no client (T2).
     */
    customerName: z.string().nullable(),
    amount: fuelMoneySchema.nullable(),
    /** النوع (legacy trash column): the product; null for debtors. */
    product: productSchema.nullable(),
    /** The family's barrel and jerrycan prices (purchase prices for purchases); null where the family has none. */
    barrelPrice: fuelMoneySchema.nullable(),
    jerrycanPrice: fuelMoneySchema.nullable(),
    deletedAt: z.iso.datetime({ offset: true }),
    deletedBy: actorSchema.nullable(),
});
export const sendMessageSchema = z.object({
    message: z.string().trim().min(1).max(1000),
    debtorIds: z.array(z.string().min(1)).min(1).max(1000),
});
// --- delivery, participants and messages (fuel.md sections 4.2 and 10) ----------
export const deliveryStatusSchema = z.enum(['PENDING', 'DELIVERED']);
export const participantRoleSchema = z.enum([
    'ENTERED_BY',
    'CASH_HANDLED_BY',
    'DELIVERED_BY',
    'EDITED_BY',
    'DELETED_BY',
    'RESTORED_BY',
    'PRICED_BY',
    'POSTED_BY',
]);
export const participantSchema = z.object({
    role: participantRoleSchema,
    user: actorSchema,
    occurredAt: z.iso.datetime({ offset: true }),
});
export const messageStatusSchema = z.enum([
    'queued',
    'sent',
    'delivered',
    'read',
    'failed',
    'invalid_phone',
]);
export const messageRowSchema = z.object({
    id: z.string(),
    kind: z.enum(['DEBT_CHARGE', 'DEBT_REPAYMENT', 'BULK', 'PDA_PURCHASE']),
    phone: z.string().nullable(),
    text: z.string(),
    status: messageStatusSchema,
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    sentAt: z.iso.datetime({ offset: true }).nullable(),
});
// --- year management (fuel.md section 5.11) ---------------------------------------
/** Every year action re-checks the employee's own password with Unified Access. */
export const yearActionSchema = z.object({
    year: z.number().int().min(2000).max(9999),
    password: z.string().min(1).max(200),
});
export const yearOperationSchema = z.object({
    id: z.string(),
    year: z.number().int(),
    kind: z.enum(['BACKUP', 'ARCHIVE_MIGRATION', 'RESET']),
    status: z.enum(['RUNNING', 'SUCCEEDED', 'FAILED']),
    verifiedAt: z.iso.datetime({ offset: true }).nullable(),
    startedBy: actorSchema,
    startedAt: z.iso.datetime({ offset: true }),
    finishedAt: z.iso.datetime({ offset: true }).nullable(),
    error: z.string().nullable(),
    /** Backups only: the file's SHA-256, for re-download and verification. */
    sha256: z.string().nullable(),
});
export const yearStatusSchema = z.object({
    frozen: z.boolean(),
    /** Every business year present in the operating database. */
    years: z.array(z.object({ year: z.number().int(), hasData: z.boolean() })),
    operations: z.array(yearOperationSchema),
});
const productTotalsSchema = z.object({
    revolvingBalances: fuelMoneySchema,
    purchases: fuelMoneySchema,
    sales: fuelMoneySchema,
    debtSales: fuelMoneySchema,
    payments: fuelMoneySchema,
    receipts: fuelMoneySchema,
});
/** The statistics are shown only after the لمحة عامة password is sent (fuel.md section 5.1). */
export const overviewRequestSchema = z.object({
    password: z.string().min(1).max(200),
    branchId: z.string().optional(),
});
export const overviewSchema = z.object({
    users: z.number().int(),
    debtors: z.number().int(),
    products: z.object({ DIESEL: productTotalsSchema, PETROL: productTotalsSchema }),
});
// --- categories and settings (fuel.md sections 5.8 and 5.9) --------------------------
export const categoryRowSchema = z.object({
    id: z.string(),
    type: containerTypeSchema,
    nameAr: z.string(),
    nameEn: z.string().nullable(),
    fraction: fractionSchema,
});
export const createCategorySchema = z.object({
    type: containerTypeSchema,
    nameAr: z.string().trim().min(1).max(100),
    nameEn: z.string().trim().min(1).max(100).optional(),
    fraction: fractionSchema,
});
const priceFields = {
    dieselBarrelSalePrice: positiveMoneySchema,
    dieselJerrycanSalePrice: positiveMoneySchema,
    petrolBarrelSalePrice: positiveMoneySchema,
    petrolJerrycanSalePrice: positiveMoneySchema,
    dieselBarrelPurchasePrice: positiveMoneySchema,
    dieselJerrycanPurchasePrice: positiveMoneySchema,
    petrolBarrelPurchasePrice: positiveMoneySchema,
    petrolJerrycanPurchasePrice: positiveMoneySchema,
    jerrycansInBarrel: fractionSchema,
    litersInJerrycan: fractionSchema,
};
/** The close password is never returned; only whether one is set. */
export const settingsSchema = z.object({
    ...priceFields,
    closePasswordSet: z.boolean(),
    overviewPasswordSet: z.boolean(),
    /** «تأكيد رقم الهاتف برمز واتساب» (fuel.md sections 5.8 and 8.3); off by default. */
    phoneVerificationEnabled: z.boolean(),
    updatedAt: z.iso.datetime({ offset: true }),
});
export const updateSettingsSchema = z.object({
    ...priceFields,
    /** Write-only; omitted or empty keeps the current password. */
    closePassword: z.string().min(4).max(200).optional(),
    /** Write-only like the close password; omitted or empty keeps the current one. */
    overviewPassword: z.string().min(4).max(200).optional(),
    /** «تأكيد رقم الهاتف برمز واتساب»; omitted keeps the current value. */
    phoneVerificationEnabled: z.boolean().optional(),
});
//# sourceMappingURL=dashboard.contract.js.map